import { Component } from "./component";

/**
 * Identity of a component as exchanged in BCF. `ifc_guid` must be a valid IfcGuid when supplied.
 * Where the consumer has no IfcGuid it should use `originating_system` and `authoring_tool_id` instead.
 *
 * @category BCF
 */
export interface ComponentIdentity {
    /** Valid IfcGuid of the component, when known */
    ifc_guid?: string;
    /** System that originated the component, e.g. 'Revit' */
    originating_system?: string;
    /** Id of the component within the originating system */
    authoring_tool_id?: string;
}

/**
 * Local identity of a product within the viewer
 *
 * @category BCF
 */
export interface ViewerIdentity {
    productId: number;
    modelId: number;
}

/**
 * Contract used to convert between viewer identity and BCF component identity.
 *
 * @category BCF
 */
export interface ViewpointIdentityResolver {
    /** Originating system label for fallback identities generated by the viewer. Defaults to `VIEWER_ORIGINATING_SYSTEM`. */
    originatingSystem?: string;

    /**
     * Converts viewer identity to BCF component identity (used when capturing a viewpoint).
     * Return an `ifc_guid` only if it is a valid IfcGuid, otherwise use the authoring fields.
     */
    toComponent(productId: number, modelId: number): ComponentIdentity | null;

    /**
     * Converts BCF component identity to viewer identity (used when restoring a viewpoint).
     * Receives all identity fields regardless of `originating_system`; the resolver decides which identities it can decode.
     * Return null when the component is not known to the viewer.
     */
    fromComponent(component: ComponentIdentity): ViewerIdentity | null;
}

/** Legacy mapper from viewer identity to a string identifier */
export type ToComponentMapper = (productId: number, modelId: number) => string;
/** Legacy mapper from an IfcGuid string to viewer identity */
export type FromComponentMapper = (guid: string) => ViewerIdentity;

/** System name used for identities generated by the viewer itself */
export const VIEWER_ORIGINATING_SYSTEM = "xBIM WebUI";

const ifcGuidPattern = /^[0-3][0-9A-Za-z_$]{21}$/;
const viewerIdPattern = /^(\d+)_(\d+)$/;

/**
 * Checks if the value is a valid (compressed, 22 characters) IfcGuid
 * @category BCF
 */
export function isIfcGuid(value: string): boolean {
    return typeof value === 'string' && ifcGuidPattern.test(value);
}

function parseViewerId(value: string): ViewerIdentity | null {
    const match = typeof value === 'string' ? viewerIdPattern.exec(value) : null;
    return match ? { modelId: parseInt(match[1], 10), productId: parseInt(match[2], 10) } : null;
}

/**
 * Parses identities generated by the viewer itself, including the legacy form where
 * '<model>_<product>' was stored in ifc_guid
 */
function parseOwnIdentity(c: ComponentIdentity, originatingSystem = VIEWER_ORIGINATING_SYSTEM): ViewerIdentity | null {
    if (c.originating_system === originatingSystem) {
        const own = parseViewerId(c.authoring_tool_id);
        if (own) {
            return own;
        }
    }
    return parseViewerId(c.ifc_guid);
}

/**
 * Normalises a legacy mapper or resolver into a function creating BCF components
 * @internal
 */
export function createComponentFactory(mapper: ToComponentMapper | ViewpointIdentityResolver | null): (productId: number, modelId: number) => Component {
    if (mapper == null) {
        return (productId, modelId) => Component.from({
            originating_system: VIEWER_ORIGINATING_SYSTEM,
            authoring_tool_id: `${modelId}_${productId}`
        });
    }
    if (typeof mapper === 'function') {
        return (productId, modelId) => {
            const id = mapper(productId, modelId);
            return isIfcGuid(id) ?
                Component.from({ ifc_guid: id }) :
                Component.from({ originating_system: VIEWER_ORIGINATING_SYSTEM, authoring_tool_id: id });
        };
    }
    const originatingSystem = mapper.originatingSystem ?? VIEWER_ORIGINATING_SYSTEM;
    return (productId, modelId) => Component.from(mapper.toComponent(productId, modelId) || {
        originating_system: originatingSystem,
        authoring_tool_id: `${modelId}_${productId}`
    });
}

/**
 * Normalises a legacy mapper or resolver into a function resolving BCF components to viewer identity
 * @internal
 */
export function createComponentResolver(mapper: FromComponentMapper | ViewpointIdentityResolver | null): (c: ComponentIdentity) => ViewerIdentity | null {
    if (mapper == null) {
        return parseOwnIdentity;
    }
    if (typeof mapper === 'function') {
        return c => {
            if (c == null) {
                return null;
            }
            const mapped = c.ifc_guid ? mapper(c.ifc_guid) : null;
            return mapped != null && !isNaN(mapped.productId) && !isNaN(mapped.modelId) ? mapped : parseOwnIdentity(c);
        };
    }
    const originatingSystem = mapper.originatingSystem ?? VIEWER_ORIGINATING_SYSTEM;
    return c => {
        if (c == null) {
            return null;
        }
        return mapper.fromComponent(c) ?? parseOwnIdentity(c, originatingSystem);
    };
}
